/*
 * Copyright (c) 2019 by Orange Polska S.A.
 * This software is the proprietary information of Orange Polska S.A.
 *
 * All rights reserved.
 */
"use strict";

(function(global) {
    interface ApiRow {
        id: number;
        ticket_number: string;
        reported_at: string;
        phishing_urls?: string[];
        phishing_domains?: string[];
        phishing_ips?: string[];
        reacted_at?: string;
        blocked_domains?: string[];
        blocked_ips?: string;
        blocked_at?: string;
        remarks?: string;
    }



    /////////////// SPCell ///////////////
    class SPCell extends HTMLTableCellElement {
        input: HTMLInputElement;
        initialValue: string;

        constructor(value: string, ...args) {
            super(...args);

            this.initialValue = value;
        }

        connectedCallback(): void {
            // do nothing
        }

        get value(): string {
            return this.input ? this.input.value || '' : '';
        }

        set editable(value: boolean): void {
            if (this.input) {
                if (value) {
                    this.input.removeAttribute('readonly');
                } else {
                    this.input.setAttribute('readonly', 'readonly');
                }
            }
        }

        detectChanges(): void {
            if (!this.input) return;

            if (this.input.value !== this.initialValue) {
                this.classList.add('changed');
            } else {
                this.classList.remove('changed');
            }
        }

        clearChanges(): void {
            if (!this.input) return;

            this.input.value = this.initialValue;

            this.detectChanges();
        }
    }


    /////////////// SPInput ///////////////
    class SPInput extends SPCell {
        constructor(value: string, ...args) {
            super(value || '', ...args);

            this.input = document.createElement('input');
            this.span = document.createElement('span');

            this.input.value = this.initialValue;
            this.span.style.paddingLeft = '2px';
        }

        connectedCallback(): void {
            super.connectedCallback();

            if (!this.span.parentElement) {
                this.appendChild(this.input);
            }

            this.input.setAttribute('type', 'text');
            this.input.setAttribute('autocomplete', 'off');

            this.input.addEventListener('input', e => {
                e.stopPropagation();
                this.detectChanges();
                this.parentElement.dispatchEvent(createEvent('change'));
            });
        }

        set editable(editable: boolean): void {
            this.input.remove();
            this.span.remove();

            if (editable) {
                this.appendChild(this.input);
            } else {
                this.appendChild(this.span).innerText = this.input.value;
            }
        }
    }



    ///////////////SPTextInput ///////////////
    class SPTextInput extends SPCell {
        constructor(value: string, ...args) {
            super(value || '', ...args);

            this.input = document.createElement('textarea');
            this.div = document.createElement('div');

            this.input.value = this.initialValue;
            this.div.style.overflowY = 'auto';
            this.div.style.setProperty('overflow-wrap', 'break-word');
            this.div.style.setProperty('word-wrap', 'break-word');
            this.div.style.setProperty('hyphens', 'auto');
            this.div.style.width = `200px`;
            this.div.style.height = `60px`;
            this.div.style.paddingLeft = '2px';
        }

        connectedCallback(): void {
            super.connectedCallback();

            if (!this.div.parentElement) {
                this.appendChild(this.input);
            }

            this.input.addEventListener('input', e => {
                e.preventDefault();
                e.stopPropagation();

                this.detectChanges();
                this.parentElement.dispatchEvent(createEvent('change'));
            });
        }

        set editable(editable: boolean): void {
            this.input.remove();
            this.div.remove();

            if (editable) {
                this.appendChild(this.input);
            } else {
                this.appendChild(this.div).innerText = this.input.value;
            }
        }
    }


    /////////////// SPArrayInput ///////////////
    class SPArrayInput extends SPCell {
        constructor(value: string[], ...args) {
            super((value || []).join("\n"), ...args);

            this.input = document.createElement('textarea');
            this.div = document.createElement('div');

            this.input.value = this.initialValue;
            this.div.style.overflowY = 'auto';
            this.div.style.setProperty('overflow-wrap', 'break-word');
            this.div.style.setProperty('word-wrap', 'break-word');
            this.div.style.setProperty('hyphens', 'auto');
            this.div.style.width = `200px`;
            this.div.style.height = `60px`;
            this.div.style.paddingLeft = '2px';
        }

        connectedCallback(): void {
            super.connectedCallback();

            if (!this.div.parentElement) {
                this.appendChild(this.input);
            }

            this.input.addEventListener('input', e => {
                e.preventDefault();
                e.stopPropagation();

                this.detectChanges();
                this.parentElement.dispatchEvent(createEvent('change'));
            });
        }

        set editable(editable: boolean): void {
            this.input.remove();
            this.div.remove();

            if (editable) {
                this.appendChild(this.input);
            } else {
                this.appendChild(this.div).innerText = this.input.value;
            }
        }

        get value(): string[] {
            const lines = this.input.value.split(/\r?\n/);
            return lines.map(v => v.trim()).filter(v => v !== '' && v.length);
        }
    }

    /////////////// SPRow ///////////////
    class SPRow extends HTMLTableRowElement {
        constructor(row: ApiRow, ...args) {
            super(...args);

            this._changes = undefined;
            this._deleted = false;
            this.refreshSubject = new rxjs.Subject();
            this.onrefresh$ = this.refreshSubject.asObservable();
            this.id = row.id || -1;
            this._editable = false;

            this.index = document.createElement('td');
            this.reactionTime = document.createElement('td');
            this.mitigationTime = document.createElement('td');
            this.ticketNumber = new SPInput(row.ticket_number);
            this.reportedAt = new SPInput(row.reported_at);
            this.phishingUrls = new SPArrayInput(row.phishing_urls);
            this.phishingDomains = new SPArrayInput(row.phishing_domains);
            this.phishingIps = new SPArrayInput(row.phishing_ips);
            this.reactedAt = new SPInput(row.reacted_at);
            this.blockedDomains = new SPArrayInput(row.blocked_domains);
            this.blockedIps = new SPArrayInput(row.blocked_ips);
            this.blockedAt = new SPInput(row.blocked_at);
            this.remarks = new SPTextInput(row.remarks);

            this.editable = this.id < 1;
        }

        disconnectedCallback(): void {
            this.release();
        }

        connectedCallback(): void {
            this.appendChild(this.index);
            this.appendChild(this.ticketNumber);
            this.appendChild(this.reportedAt);
            this.appendChild(this.phishingUrls);
            this.appendChild(this.phishingDomains);
            this.appendChild(this.phishingIps);
            this.appendChild(this.reactedAt);
            this.appendChild(this.reactionTime);
            this.appendChild(this.blockedDomains);
            this.appendChild(this.blockedIps);
            this.appendChild(this.blockedAt);
            this.appendChild(this.mitigationTime);
            this.appendChild(this.remarks);

            this.addEventListener('change', e => {
                e.preventDefault();
                e.stopPropagation();
                this.calculateTimes();
                this.calculateChanges();
                this.refreshSubject.next({ id: this.id, changes: this.changes });
            });

            setTimeout(() => {
                this.calculateTimes();
            });
        }

        set editable(value: boolean): void {
            this._editable = value;

            for (const key of Object.keys(this)) {
                const child = this[key];

                if (child instanceof SPCell) {
                    child.editable = value;
                }
            }
        }

        get editable(): boolean {
            return !!this._editable;
        }

        set deleted(value) {
            if (value) {
                this.remove();
            } else {
                this._deleted = false;

                if (this.id > 0) {
                    this.parentElement.style.display = 'table-row-group';
                    this.style.display = 'table-row';
                }
            }
        }

        get deleted(): boolean {
            return !!this._deleted;
        }

        release(): void {
            this.refreshSubject.complete();
        }

        clearChanges(): void {
            for (const child of Array.from(this.childNodes)) {
                if (child instanceof SPCell) {
                    child.clearChanges();
                }
            }

            this.calculateChanges();
            this.calculateTimes();
            this.refreshSubject.next({ id: this.id, changes: this.changes });
        }

        remove(): void {
            const container = this.parentElement;
            this._deleted = true;

            if (this.id > 0) {
                this.style.display = 'none';
            } else {
                super.remove();
            }

            // this.release();
            container.dispatchEvent(createEvent('row-removed'));
        }

        reindex(index: number): void {
            this.index.innerText = index;
        }

        get changed(): boolean {
            return this._changes !== undefined;
        }

        get changes(): Object {
            return this._changes || {};
        }

        getReactionTime(): number {
            return this.reactionTimeValue;
        }

        getMitigationTime(): number {
            return this.mitigationTimeValue;
        }

        getPhishingUrls(): string[] {
            return this.phishingUrls.value;
        }

        getPhishingDomains(): string[] {
            return this.phishingDomains.value;
        }

        getPhishingIps(): string[] {
            return this.phishingIps.value;
        }

        getBlockedDomains(): string[] {
            return this.blockedDomains.value;
        }

        getBlockedIps(): string[] {
            return this.blockedIps.value;
        }

        get value(): Object {
            return {
                ticket_number: this.ticketNumber.value || '',
                reported_at: this.reportedAt.value || '',
                phishing_urls: this.phishingUrls.value || [],
                phishing_domains: this.phishingDomains.value || [],
                phishing_ips: this.phishingIps.value || [],
                reacted_at: this.reactedAt.value || '',
                blocked_domains: this.blockedDomains.value || [],
                blocked_ips: this.blockedIps.value || [],
                blocked_at: this.blockedAt.value || '',
                remarks: this.remarks.value || ''
            };
        }

        calculateTimes(): void {
            const reported_at = new Date(this.reportedAt.value.replace(' ', 'T'));
            const reacted_at = new Date(this.reactedAt.value.replace(' ', 'T'));
            const blocked_at = new Date(this.blockedAt.value.replace(' ', 'T'));

            if (Number.isNaN(reported_at.getTime()) || Number.isNaN(reacted_at.getTime())) {
                this.reactionTime.innerText = '-';
                delete this.reactionTimeValue;
            } else {
                const diff = Math.ceil(((reacted_at.getTime() - reported_at.getTime()) / 1000) / 60); // w minutach
                this.reactionTime.innerText = diff.toTime();
                this.reactionTimeValue = diff;
            }

            if (Number.isNaN(reported_at.getTime()) || Number.isNaN(blocked_at.getTime())) {
                this.mitigationTime.innerText = '-';
                delete this.mitigationTimeValue;
            } else {
                const diff2 = Math.ceil(((blocked_at.getTime() - reported_at.getTime()) / 1000) / 60); // w minutach
                this.mitigationTime.innerText = diff2.toTime();
                this.mitigationTimeValue = diff2;
            }
        }

        calculateChanges(): void {
            let changes = {};
            let changed = false;

            for (const key of Object.keys(this)) {
                const obj = this[key];

                if (obj && typeof obj === 'object' && obj instanceof SPCell) {
                    if (obj.classList.contains('changed')) {
                        changes[key.toSnake()] = obj.value;
                        changed = true;
                    }
                }
            }

            if (changed) {
                this._changes = changes;
            } else {
                delete this._changes;
            }
        }
    }


    /////////////// SPContainer ///////////////
    class SPContainer {
        constructor(rows$: rxjs.Observable) {
            this.container = document.getElementById('sp-content');
            this.statistics$ = new rxjs.Subject();
            this.changed$ = new rxjs.Subject();
            this.rows$ = rows$;

            // noinspection JSUnresolvedFunction
            this.rows$.subscribe((rows: ApiRow[]) => {
                let child;

                // noinspection JSAssignmentUsedAsCondition
                while (child = this.container.firstChild) {
                    if (child instanceof SPRow) {
                        child.release();
                    }

                    this.container.removeChild(child);
                }

                let index = 1;

                for (const row of (rows || [])) {
                    const spRow = new SPRow(row);
                    spRow.reindex(index++);

                    this.container.appendChild(spRow);

                    spRow.onrefresh$.subscribe(() => {
                        setTimeout(() => {
                            this.recalculateStatistics();
                        });
                    });
                }

                if ((rows || []).length) this.container.style.display = 'table-row-group';
                else this.container.style.display = 'none';

                setTimeout(() => {
                    this.recalculateStatistics();
                });
            });

            this.container.addEventListener('row-removed', () => this.afterRowRemoved());
        }

        recalculateStatistics(): void {
            const result = {
                phishingUrlsTotalCount: 0,
                phishingDomainsTotalCount: 0,
                phishingIpsTotalCount: 0,
                blockedDomainsTotalCount: 0,
                blockedIpsTotalCount: 0,
                reactionTimeAvg: '0:00',
                reactionTimeMax: '0:00',
                mitigationTimeAvg: '0:00',
                mitigationTimeMax: '0:00'
            };

            let reactionTimeMax = 0;
            let mitigationTimeMax = 0;
            let reactionsCount = 0;
            let mitigationsCount = 0;
            let reactionsTotal = 0;
            let mitigationsTotal = 0;
            let changed = false;

            for (const child of Array.from(this.container.childNodes)) {
                if (!(child instanceof SPRow)) continue;
                if (child.deleted) {
                    changed = true;
                    continue;
                }

                changed = changed || child.changed;
                result.phishingUrlsTotalCount += child.getPhishingUrls().length;
                result.phishingDomainsTotalCount += child.getPhishingDomains().length;
                result.phishingIpsTotalCount += child.getPhishingIps().length;
                result.blockedDomainsTotalCount += child.getBlockedDomains().length;
                result.blockedIpsTotalCount += child.getBlockedIps().length;

                if (child.getReactionTime() !== undefined) {
                    if (child.getReactionTime() > reactionTimeMax) {
                        reactionTimeMax = child.getReactionTime();
                    }

                    reactionsCount++;
                    reactionsTotal += child.getReactionTime();
                }

                if (child.getMitigationTime() !== undefined) {
                    if (child.getMitigationTime() > mitigationTimeMax) {
                        mitigationTimeMax = child.getMitigationTime();
                    }

                    mitigationsCount++;
                    mitigationsTotal += child.getMitigationTime();
                }
            }

            result.reactionTimeMax = reactionTimeMax.toTime();
            result.mitigationTimeMax = mitigationTimeMax.toTime();
            result.reactionTimeAvg = (reactionsCount > 0 ? Math.floor(reactionsTotal / reactionsCount) : 0).toTime();
            result.mitigationTimeAvg = (mitigationsCount > 0 ? Math.floor(mitigationsTotal / mitigationsCount) : 0).toTime();

            this.statistics$.next(result);
            this.changed$.next(changed);
        }

        get rows(): SPRow[] {
            const result = [];

            for (const child of Array.from(this.container.childNodes)) {
                if (!(child instanceof SPRow) || child.deleted) continue;

                result.push(child);
            }

            return result;
        }

        get allRows(): SPRow[] {
            const result = [];

            for (const child of Array.from(this.container.childNodes)) {
                if (!(child instanceof SPRow)) continue;

                result.push(child);
            }

            return result;
        }

        enterEditMode(): void {
            for (const row of this.rows) {
                row.editable = true;
            }
        }

        exitEditMode(): void {
            for (const row of this.rows) {
                if (row.id > 0) {
                    row.editable = false;
                }
            }
        }

        cancelEditMode(): void {
            for (const row of this.allRows) {
                if (row.id > 0) {
                    row.editable = false;
                    row.deleted = false;
                    row.clearChanges();
                }
            }
        }

        addEmptyRow(): void {
            const spRow = new SPRow({});
            spRow.reindex(this.rows.length + 1);

            this.container.appendChild(spRow);
            spRow.onrefresh$.subscribe(() => {
                setTimeout(() => {
                    this.recalculateStatistics();
                });
            });

            this.container.style.display = 'table-row-group';
        }

        afterRowRemoved(): void {
            this.recalculateStatistics();

            if (this.rows.length) this.container.style.display = 'table-row-group';
            else this.container.style.display = 'none';

            let index = 1;

            for (const row of this.rows) {
                row.reindex(index);
            }
        }

        hasChangesToExistingRows(): boolean {
            for (const child of Array.from(this.container.childNodes)) {
                if (!(child instanceof SPRow) || (!child.changed && !child.deleted)) continue;

                if (child.deleted) {
                    return true;
                } else if (child.id && child.id > 0) {
                    if (child.changed) {
                        return true;
                    }
                }
            }

            return false;
        }

        hasChanges(): boolean {
            for (const child of Array.from(this.container.childNodes)) {
                if (!(child instanceof SPRow) || (!child.changed && !child.deleted)) continue;

                if (child.deleted) {
                    return true;
                } else if (child.id && child.id > 0) {
                    if (child.changed) {
                        return true;
                    }
                } else {
                    return true;
                }
            }

            return false;
        }

        get changes(): Object {
            const changes = [];

            for (const child of Array.from(this.container.childNodes)) {
                if (!(child instanceof SPRow) || (!child.changed && !child.deleted)) continue;

                if (child.deleted) {
                    changes.push({
                        id: child.id,
                        deleted: true
                    });
                } else if (child.id && child.id > 0) {
                    changes.push(Object.assign({id: child.id, deleted: false}, child.changes));
                } else {
                    changes.push(Object.assign({deleted: false}, child.value));
                }
            }

            return changes;
        }
    }


    /////////////// SPStatistics ///////////////
    class SPStatistics {
        constructor(stats$) {
            this.phishingUrlsTotalCount = document.getElementById('phishing_urls_total_count');
            this.phishingDomainsTotalCount = document.getElementById('phishing_domains_total_count');
            this.phishingIpsTotalCount = document.getElementById('phishing_ips_total_count');
            this.blockedDomainsTotalCount = document.getElementById('blocked_domains_total_count');
            this.blockedIpsTotalCount = document.getElementById('blocked_ips_total_count');
            this.reactionTimeAvg = document.getElementById('reaction_time_avg');
            this.reactionTimeMax = document.getElementById('reaction_time_max');
            this.mitigationTimeAvg = document.getElementById('mitigation_time_avg');
            this.mitigationTimeMax = document.getElementById('mitigation_time_max');

            stats$.subscribe((stats: Object) => {
                this.phishingUrlsTotalCount.innerText = (stats.phishingUrlsTotalCount || 0).toString();
                this.phishingDomainsTotalCount.innerText = (stats.phishingDomainsTotalCount || 0).toString();
                this.phishingIpsTotalCount.innerText = (stats.phishingIpsTotalCount || 0).toString();
                this.blockedDomainsTotalCount.innerText = (stats.blockedDomainsTotalCount || 0).toString();
                this.blockedIpsTotalCount.innerText = (stats.blockedIpsTotalCount || 0).toString();
                this.reactionTimeAvg.innerText = stats.reactionTimeAvg || '0:00';
                this.reactionTimeMax.innerText = stats.reactionTimeMax || '0:00';
                this.mitigationTimeAvg.innerText = stats.mitigationTimeAvg || '0:00';
                this.mitigationTimeMax.innerText = stats.mitigationTimeMax || '0:00';
            });
        }
    }


    /////////////// SPMenu ///////////////
    class SPMenuAction extends HTMLDivElement {
        constructor(label: string, icon: string, action: (e: MouseEvent) => any, ...args) {
            super(...args);

            this.label = label;
            this.icon = icon;
            this.action = action;
        }

        connectedCallback() {
            const icon = document.createElement('i');
            icon.classList.add('md', `md-${this.icon}`);

            const label = document.createElement('span');
            label.innerText = this.label;

            this.appendChild(icon);
            this.appendChild(label);

            this.addEventListener('click', this.action);
        }
    }

    class SPMenu {
        constructor() {
            this.menu = null;
            this.overlay = null;

            window.addEventListener('contextmenu', (e: MouseEvent) => {
                this.hide();

                let target = e.target;

                while (target && !(target instanceof SPRow)) {
                    target = target.parentElement;
                }

                if (!target) return;

                if (this.show(target, e.pageX, e.pageY)) {
                    e.preventDefault();
                }
            });
        }

        hide(): void {
            if (this.menu) {
                this.menu.remove();
                this.menu = null;
            }

            if (this.overlay) {
                this.overlay.remove();
                this.overlay = null;
            }
        }

        show(row: SPRow, x: number, y: number): boolean {
            if (this.menu) {
                this.hide();
            }

            let actions = [];

            if (row.changed) {
                actions.push(new SPMenuAction(row.id > 0 ? 'Wycofaj zmiany' : "Wyczyść", 'clear', () => row.clearChanges()));
            }

            if (row.editable) {
                actions.push(new SPMenuAction('Usuń wiersz', 'delete', () => {
                    const popup = new HTMLElements.PopupElement();

                    if (row.id > 0) {
                        popup.content = 'Czy na pewno chcesz usunąć wiersz? Operację można wycofać odświeżając dane lub klikając w przycisk „Anuluj”.';
                    } else {
                        popup.content = 'Wiersz zostanie bezpowrotnie usunięty. Czy chcesz kontynuować?';
                    }
                    popup.addDefaultButton('Tak', () => {
                        row.remove();
                        popup.hide();
                    });
                    popup.addButton('Nie', () => popup.hide());
                    popup.show();
                }));
            }

            if (actions.length === 0) {
                return false;
            }

            document.body.appendChild(this.overlay = document.createElement('div'));
            document.body.appendChild(this.menu = document.createElement('div'));
            this.overlay.classList.add('overlay');
            this.menu.classList.add('contextmenu');
            this.menu.style.top = `${y}px`;
            this.menu.style.left = `${x}px`;
            this.overlay.addEventListener('click', () => this.hide());
            this.menu.addEventListener('click', () => this.hide());

            for (const action of actions) {
                this.menu.appendChild(action);
            }

            return true;
        }
    }


    /////////////// MessagesContainer ///////////////
    class MessageContainerElement extends HTMLDivElement {
        static TypeNotice = 'notice';
        static TypeWarning = 'warning';

        constructor(type: string, messages: string[], ...args) {
            super(...args);

            this.type = type || MessageContainerElement.TypeNotice;
            this.messages = messages || [];
        }

        connectedCallback() {
            let msg;

            switch (this.type) {
                case MessageContainerElement.TypeNotice:
                    msg = this.messages.length ? 'Podczas analizy wykryto potencjalne problemy:' : 'Wystąpił niezidentyfikowany problem.';
                    break;
                case MessageContainerElement.TypeWarning:
                    msg = this.messages.length ? 'Wystąpiły następujące błędy:' : 'Wystąpił niezidentyfikowany problem.';
                    break;
            }

            this.appendChild(document.createTextNode(msg));

            if (this.messages.length) {
                const ul = document.createElement('ul');

                for (const err of this.messages) {
                    const li = document.createElement('li');
                    li.innerText = err;

                    ul.appendChild(li);
                }

                this.appendChild(ul);
            }
        }
    }

    /////////////// Controller ///////////////
    // noinspection JSUnresolvedVariable
    class StopPhishingBlockadeRequestsController {
        constructor(config) {
            this.rows$ = new rxjs.Subject();

            this.table = document.getElementById('sp-table');
            this.content = document.getElementById('sp-content');
            this.newRecord = document.getElementById('new-record');
            this.toggleEditMode = document.getElementById('edit-mode');
            this.save = document.getElementById('save-changes');
            this.container = new SPContainer(this.rows$.asObservable());
            this.statistics = new SPStatistics(this.container.statistics$.asObservable());
            this.menu = new SPMenu();

            this.lastFilters = {
                company: undefined,
                from: undefined,
                to: undefined
            };

            this.lastUpdateToken = undefined;

            this.init(this.config = config);

            global.addEventListener('beforeunload', e => {
                if (this.container.hasChanges()) {
                    e.preventDefault();
                    e.returnValue = '';
                }
            })
        }

        init(config: Object): void {
            const companyPicker = document.getElementById('company_picker');

            document.getElementById('filter-button').addEventListener('click', e => {
                e.preventDefault();

                const company = Number.isInteger(+companyPicker.value) ? +companyPicker.value : null;
                const from = document.getElementById('from-date').value;
                const to = document.getElementById('to-date').value;

                if (company === null) {
                    return;
                }

                if (this.container.hasChanges()) {
                    const popup = new HTMLElements.PopupElement();
                    popup.content = 'Niezapisane zmiany zostaną utracone. Czy chcesz kontynuować?';
                    popup.skippable = true;
                    popup.addDefaultButton('Tak', () => {
                        this.lastFilters.company = company;
                        this.lastFilters.from = from;
                        this.lastFilters.to = to;

                        this.load();
                        popup.hide();
                    });
                    popup.addCancelButton('Nie', () => popup.hide());
                    popup.show();
                } else {
                    this.lastFilters.company = company;
                    this.lastFilters.from = from;
                    this.lastFilters.to = to;

                    this.load();
                }
            });

            document.getElementById('export-button').addEventListener('click', e => {
                e.preventDefault();
                e.stopPropagation();

                const company = Number.isInteger(+companyPicker.value) ? +companyPicker.value : null;
                const from = document.getElementById('from-date').value;
                const to = document.getElementById('to-date').value;

                if (company === null) {
                    return;
                }

                window.open(`${config.export_url}?${$.param({company, from, to})}`, '_self');
            });

            this.container.changed$.asObservable().subscribe((changed: boolean) => {
                if (changed) {
                    this.save.removeAttribute('disabled');
                    this.save.innerText = 'Zapisz zmiany';
                } else {
                    this.save.setAttribute('disabled', 'disabled');
                    this.save.innerText = 'Brak zmian';
                }
            });

            this.toggleEditMode.addEventListener('click', (e: MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();

                if (this.toggleEditMode.inEditMode) {
                    if (this.container.hasChangesToExistingRows()) {
                        const popup = new HTMLElements.PopupElement();
                        popup.closeable = false;
                        popup.content = 'Anulowanie spowoduje wycofanie zmian w już zapisanych wpisach, które zostały wprowadzone w trybie edycji. Czy chcesz kontynuować?';
                        popup.addButton('Tak', () => {
                            this.container.cancelEditMode();
                            popup.hide();
                            this.toggleEditMode.innerText = 'Edytuj';
                            delete this.toggleEditMode.inEditMode;
                        });
                        popup.addCancelButton('Nie', () => popup.hide());
                        popup.show();
                    } else {
                        this.container.cancelEditMode();
                        this.toggleEditMode.innerText = 'Edytuj';
                        delete this.toggleEditMode.inEditMode;
                    }
                } else {
                    const popup = new HTMLElements.PopupElement();
                    popup.closeable = false;
                    popup.content = 'Wchodzisz w tryb edycji, umożliwiający wprowadzanie zmian w już zapisanych wpisach. Czy chcesz kontynuować?';
                    popup.skippable = true;
                    popup.addDefaultButton('Tak', () => {
                        this.container.enterEditMode();
                        popup.hide();
                        this.toggleEditMode.innerText = 'Anuluj';
                        this.toggleEditMode.inEditMode = true;
                    });
                    popup.addCancelButton('Nie', () => popup.hide());
                    popup.show();
                }
            });

            this.newRecord.addEventListener('click', (e: MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();

                this.container.addEmptyRow();
            });

            this.save.addEventListener('click', (e: MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();

                const popup = new HTMLElements.PopupElement();
                popup.content = 'Czy na pewno chcesz zapisać wprowadzone zmiany?';
                popup.addDefaultButton('Tak', () => {
                    this.saveAll(this.container.changes);
                    popup.hide();
                });
                popup.addCancelButton('Nie', () => popup.hide());
                popup.show();
            });
        }

        saveAll(changes: Object, force: boolean = false): void {
            // {id: 123} - delete
            // {id: 123, other_fields:...} - update
            // {other_fields: ...} - insert
            const request = {
                company_id: this.lastFilters.company,
                requests: changes,
                token: this.lastUpdateToken,
                force
            };

            ApiConnect.post(this.config.update_url, request).then(response => {
                if (response.status === 'success') {
                    this.load(true);
                } else if (response.status === 'notice') {
                    const popup = new HTMLElements.PopupElement();
                    popup.header = 'Ostrzeżenie';
                    popup.closeable = false;
                    popup.content = new MessageContainerElement(MessageContainerElement.TypeNotice, response.messages || []);
                    popup.addCancelButton('Chcę poprawić', () => popup.hide());
                    popup.addButton('Zapisz i zamknij te głupoty', () => {
                        this.saveAll(changes, true);
                        popup.hide();
                    });
                    popup.show();
                } else {
                    const popup = new HTMLElements.PopupElement();
                    popup.header = 'Błąd';
                    popup.closeable = false;
                    popup.content = new MessageContainerElement(MessageContainerElement.TypeWarning, response.messages || []);
                    popup.addCancelButton('Już poprawiam', () => popup.hide());
                    popup.show();
                }
            }).catch(err => console.error('Błąd zapisywania danych: ', err));
        }

        load(reload: boolean = false): void {
            const filterBtn = document.getElementById('filter-button');
            filterBtn.disabled = true;

            ApiConnect.get(this.config.data_url, this.lastFilters).then(data => {
                this.table.style.display = 'table';
                this.rows$.next((((data || {}).data || {}).blockade_requests || {}).rows || []);
                this.lastUpdateToken = (((data || {}).data || {}).blockade_requests || {}).csrf_token;
                this.toggleEditMode.innerText = 'Edytuj';
                delete this.toggleEditMode.inEditMode;
                if (reload) {
                    setTimeout(() => {
                        this.save.innerText = 'Zapisano';
                    });
                }
            }).catch(err => console.error('Błąd wczytywania danych: ', err), () => filterBtn.disabled = false);
        }
    }

    // Rejestracja customElements
    customElements.define('sp-cell', SPCell, {extends: 'td'});
    customElements.define('sp-input', SPInput, {extends: 'td'});
    customElements.define('sp-text-input', SPTextInput, {extends: 'td'});
    customElements.define('sp-array-input', SPArrayInput, {extends: 'td'});
    customElements.define('sp-row', SPRow, {extends: 'tr'});
    customElements.define('sp-menu-action', SPMenuAction, {extends: 'div'});
    customElements.define('sp-popup-messages', MessageContainerElement, {extends: 'div'});

    //wyeksportowanie klasy
    global.NS = global.NS || {};
    global.NS.Views = global.NS.Views || {};
    global.NS.Views.StopPhishingBlockadeRequestsController = StopPhishingBlockadeRequestsController || {};
})(window);
